
import { defineComponent } from 'vue';
import OutlineButton from '@/components/ui/OutlineButton.vue';
import ProposalRow from '@/components/proposal/list/ProposalRow.vue';
import ContentLoader from '@/components/ui/ContentLoader.vue';
import { ProposalAction } from '@/store/proposal/proposal.action';
import { ProposalMutation } from '@/store/proposal/proposal.mutation';
import { mapGetters } from 'vuex';
import SearchInput from '@/components/ui/form/SearchInput.vue';
import { ProposalCustomer } from '@/model/proposal-customer';
import ProposalsListFilters, { ProposalsFilters } from '@/components/proposal/list/ProposalsListFilters.vue';
import { ProposalStatus } from '@/model/proposal-status';
import { Supplier } from '@/model/supplier';

export default defineComponent({
  name: "ProposalsList",
  components: {ProposalsListFilters, SearchInput, ContentLoader, ProposalRow, OutlineButton},
  mounted(): void {
    const q = this.$route.query.search;
    if (q) this.search = Array.isArray(q) ? q[0] ?? '' : q;
    // Serve the in-memory list instantly when it's already loaded and unchanged (e.g. navigating
    // back from a proposal). It's only refetched on the first load or after a proposal is
    // created/edited/archived/deleted (those actions clear listLoaded).
    if (!this.listLoaded) {
      this.$store.dispatch(ProposalAction.FETCH_LIST_ELEMENTS);
    }
  },
  data() {
    return {
      showFiltersModal: false,
      filters: {
        status: [
          ProposalStatus.ACCEPTED,
          ProposalStatus.DRAFT,
          ProposalStatus.AWAITING,
          ProposalStatus.REJECTED,
          ProposalStatus.PRODUCTS_USED_NOT_OFFERED
        ]
      },
      showFilters: false
    }
  },
  methods: {
    handleFiltersChange(filters: ProposalsFilters): void {
      this.filters = JSON.parse(JSON.stringify(filters));
    }
  },
  watch: {
    // Archived proposals aren't loaded by default (keeps the initial list light). The first time
    // the user enables the Archived filter, fetch them too — from then on every refresh includes
    // them, so client-side search/filter stay instant over the full set.
    'filters.status'(statuses: string[]): void {
      if (statuses.includes(ProposalStatus.ARCHIVED) && !this.includeArchived) {
        this.$store.commit(ProposalMutation.SET_INCLUDE_ARCHIVED, true);
        this.$store.dispatch(ProposalAction.FETCH_LIST_ELEMENTS);
      }
    }
  },
  computed: {
    ...mapGetters(['proposalList', 'isProposalsProcessing', 'listSearch', 'includeArchived', 'listLoaded']),
    // Backed by the store so the term survives navigating into a proposal and back.
    search: {
      get(): string {
        return this.listSearch;
      },
      set(value: string): void {
        this.$store.commit(ProposalMutation.SET_LIST_SEARCH, value);
      }
    },
    filteredProposals(): ProposalCustomer[] {
      if (!this.proposalList) {
        return [];
      }

      const proposals = this
          .proposalList
          .filter((proposal: ProposalCustomer) => this.filters.status.includes(proposal.status));

      if (!this.search) {
        return proposals;
      }

      const term = this.search.toLowerCase();

      return proposals
          .filter((proposal: ProposalCustomer) => {
            // `suppliers` is typed as string but the list endpoint returns [{ fullName }], as rendered in ProposalRow
            const suppliers = (proposal.suppliers as unknown as Supplier[] | undefined) ?? [];

            return proposal.customer.name.toLowerCase().includes(term)
                || proposal.id?.toString().includes(term)
                || proposal.description?.toLowerCase().includes(term)
                || proposal.createdBy?.toLowerCase().includes(term)
                || proposal.requestedBy?.toLowerCase().includes(term)
                || (Array.isArray(suppliers) && suppliers.some((supplier) => supplier.fullName?.toLowerCase().includes(term)));
          });
    }
  }
})
